import { Component, DestroyRef, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatTableDataSource } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from 'moment';
import { default as _rollupMoment, Moment } from 'moment';
import { UserEngagementService } from '../../service/user-engagement.service';
import { ErrorMsg, SnackService } from '../../service/snack.service';
import { UserEngagement, UserEngagementMonthlyData } from '../../lib/interfaces/user-engagement.interface';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    merge,
    Observable,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { getMonthsYearBetweenDates, MonthYear } from '../../helpers/get-months-between-dates.helper';

const moment = _rollupMoment || _moment;
const RANGE_START_DATE = moment().startOf('month').subtract(3, 'months');
const RANGE_END_DATE = moment().endOf('month');;
const DATASETS = [
    {
        label: 'Single User',
        key: 'singleUser',
    },
    {
        label: 'User',
        key: 'user',
    },
    {
        label: 'Coach',
        key: 'coach',
    },
    {
        label: 'Founder',
        key: 'founder',
    },
    {
        label: 'Researcher',
        key: 'researcher',
    },
];

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
const MM_YYYY_FORMAT = {
    parse: {
        dateInput: 'MM/YYYY',
    },
    display: {
        dateInput: 'MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

interface TransformedRow {
    timeframe: UserEngagement['timeframe'];
    [key: string]: number | string; // Dynamic keys for dataset_month_year
}

@Component({
    selector: 'app-user-report',
    standalone: false,
    templateUrl: './user-report.component.html',
    styleUrl: './user-report.component.scss',
    providers: [
        provideMomentDateAdapter(MM_YYYY_FORMAT),
    ],
})
export class UserReportComponent {
    protected range = new FormGroup({
        start: new FormControl<Moment>(
            RANGE_START_DATE,
            {
                nonNullable: true,
                validators: [Validators.required],
                updateOn: 'blur',
            },
        ),
        end: new FormControl<Moment>(
            RANGE_END_DATE,
            {
                nonNullable: true,
                validators: [
                    Validators.required,
                ],
                updateOn: 'blur',
            },
        ),
    });

    protected datasets = DATASETS;
    protected monthYears: MonthYear[] = [];
    protected displayedColumns: string[] = [];
    protected dataSources = new MatTableDataSource<TransformedRow>([]);
    protected columnGroups: Array<{ label: string; key: string; columns: string[]; }> = [];
    protected displayedColumnGroups: string[] = [];
    protected minDate = moment([2025, 0, 1]);
    protected maxDate = RANGE_END_DATE;

    private userEngagementService = inject(UserEngagementService);
    private destroyRef = inject(DestroyRef);
    private snackService = inject(SnackService);

    public ngOnInit(): void {
        this.loadUserEngagement()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();

        this.dateRangeListener();
    }

    private loadUserEngagement(): Observable<UserEngagement[]> {
        if (
            this.range.controls.start.invalid &&
            this.range.controls.start.errors?.['matDatepickerMin']
        ) {
            this.snackService.error({
                headline: 'Error',
                text: `Start date must be after ${this.minDate.format('MM/YYYY')}`,
            });

            return throwError(() => <ErrorMsg>{ headline: 'Error', text: 'Start date invalid' });
        }

        if (
            this.range.controls.end.invalid &&
            this.range.controls.end.errors?.['matDatepickerMax']
        ) {
            this.snackService.error({
                headline: 'Error',
                text: 'End date must not be in the future',
            });

            return throwError(() => <ErrorMsg>{ headline: 'Error', text: 'End date invalid' });
        }

        const { start, end } = this.range.value;

        const startMonth = typeof start?.month() === 'number' ? start.month() + 1 : null;
        const startYear = typeof start?.year() === 'number' ? start.year() : null;
        const endMonth = typeof end?.month() === 'number' ? end.month() + 1 : null;
        const endYear = typeof end?.year() === 'number' ? end.year() : null;

        if (
            [startMonth, startYear, endMonth, endYear].some((v) => v === null) ||
            !start ||
            !end
        ) {
            this.snackService.error({ headline: 'Error', text: 'Start and end date are required' });

            return throwError(() => <ErrorMsg>{ headline: 'Error', text: 'Start and end date are required' });
        }

        this.monthYears = getMonthsYearBetweenDates(
            start,
            end,
        );


        this.dataSources.data = [];
        this.displayedColumns = [];
        this.displayedColumnGroups = [];
        this.columnGroups = [];

        return this.userEngagementService.getUserEngagement(
            startMonth,
            startYear,
            endMonth,
            endYear,
        )
            .pipe(
                catchError((error) => {
                    this.snackService.error(error);

                    return throwError(() => error);
                }),
                tap((userEngagement) => this.generateTableContent(userEngagement)),
            );
    }

    private dateRangeListener(): void {
        const listener = () => merge(
            this.range.controls.start.valueChanges
                .pipe(
                    debounceTime(500),
                    distinctUntilChanged(),
                ),
            this.range.controls.end.valueChanges
                .pipe(
                    debounceTime(500),
                    distinctUntilChanged(),
                ),
        )
            .pipe(
                switchMap(() => this.loadUserEngagement()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                error: () => listener(),
            });

        listener();
    }

    private generateTableContent(
        userEngagement: UserEngagement[],
    ): void {
        const data: TransformedRow[] = userEngagement.map(row => {
            const transformed: TransformedRow = { timeframe: row.timeframe };
            this.datasets.forEach(dataset => {
                this.monthYears.forEach(({ month, year }) => {
                    const key = `${dataset.key}_${month.toLowerCase()}_${year}`;

                    const dateEntry = row.data.find(entry => entry.month === month && entry.year === year);
                    transformed[key] = dateEntry ? dateEntry[dataset.key as keyof UserEngagementMonthlyData] : 0;
                });
            });

            return transformed;
        });

        this.displayedColumns = [
            'timeframe',
            ...this.datasets.flatMap(
                (d) => this.monthYears.map(({ month, year }) => `${d.key}_${month.toLowerCase()}_${year}`),
            ),
        ];

        this.columnGroups = this.datasets.map((d) => ({
            label: d.label,
            key: d.key,
            columns: this.monthYears.map(({ month, year }) => `${d.key}_${month.toLowerCase()}_${year}`),
        }));

        this.displayedColumnGroups = [
            'timeframe',
            ...this.columnGroups.map((d) => `${d.key}_group`),
        ];

        console.log('data', data);
        this.dataSources.data = data;
    }
}

